import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { shiftType: String, duration: Number, staffRequired: Number }
  static targets = [
    "percentageField",
    "absoluteField",
    "valueLabel",
    "unitLabel",
    "hint",
    "infoText"
  ]

  connect() {
    const shiftTypeSelect = document.getElementById("team_shift_type")
    if (shiftTypeSelect) {
      shiftTypeSelect.addEventListener("change", this.toggleSection.bind(this))
    }
    this.toggleSection()
    this.initializeMode()

    // Listen for value changes to update info box
    const pctField = document.querySelector("[name='shift[or_reduction_percentage]']")
    const valField = document.querySelector("[name='shift[or_reduction_value]']")
    if (pctField) pctField.addEventListener("input", () => this.updateInfoText())
    if (valField) valField.addEventListener("input", () => this.updateInfoText())
  }

  initializeMode() {
    const checkedRadio = this.element.querySelector("input[name='shift[or_reduction_mode]']:checked")
    if (checkedRadio) {
      const mode = checkedRadio.value
      if (mode === "percentage") {
        this.showPercentageField()
      } else {
        this.showAbsoluteField(mode)
      }
    }
    this.updateInfoText()
  }

  toggleSection() {
    const shiftTypeSelect = document.getElementById("team_shift_type")
    if (!shiftTypeSelect) return

    const selectedType = shiftTypeSelect.value.toLowerCase()
    const section = document.getElementById("or_reduction_section")
    if (!section) return

    if (selectedType.includes("or") || selectedType.includes("surgery")) {
      section.classList.remove("hidden")
    } else {
      section.classList.add("hidden")
      this.clearReductionFields()
    }
  }

  modeChanged(event) {
    const mode = event.target.value

    if (mode === "percentage") {
      this.showPercentageField()
    } else {
      this.showAbsoluteField(mode)
    }
    this.updateInfoText()
  }

  showPercentageField() {
    if (this.hasPercentageFieldTarget) this.percentageFieldTarget.classList.remove("hidden")
    if (this.hasAbsoluteFieldTarget) this.absoluteFieldTarget.classList.add("hidden")
    this.updateLabels("percentage")
  }

  showAbsoluteField(mode) {
    if (this.hasPercentageFieldTarget) this.percentageFieldTarget.classList.add("hidden")
    if (this.hasAbsoluteFieldTarget) this.absoluteFieldTarget.classList.remove("hidden")
    this.updateLabels(mode)
  }

  updateLabels(mode) {
    const config = {
      percentage: { label: "Reduction Percentage", unit: "%", hint: "(e.g., 20% reduces 10 ORs to 8 ORs)" },
      slots: { label: "Slots to Reduce Per Day", unit: "slots/day", hint: "(e.g., 1 = one fewer slot assigned each day)" },
      hours: { label: "Hours to Reduce", unit: "hours", hint: "(e.g., 2 = 8h shift becomes 6h during period)" }
    }[mode] || { label: "Reduction Percentage", unit: "%", hint: "(e.g., 20% reduces 10 ORs to 8 ORs)" }

    if (this.hasValueLabelTarget) this.valueLabelTarget.textContent = config.label
    if (this.hasUnitLabelTarget) this.unitLabelTarget.textContent = config.unit
    if (this.hasHintTarget) this.hintTarget.textContent = config.hint
  }

  updateInfoText() {
    if (!this.hasInfoTextTarget) return

    const mode = this.currentMode()
    const duration = this.durationValue || 8
    const staffRequired = this.staffRequiredValue || 1

    if (mode === "percentage") {
      const pct = parseFloat(document.querySelector("[name='shift[or_reduction_percentage]']")?.value) || 0
      if (pct > 0) {
        const reduced = Math.round(staffRequired * (1 - pct / 100))
        this.infoTextTarget.innerHTML = `<strong>Summary:</strong> ${pct}% reduction means ${reduced} of ${staffRequired} slots will be assigned per day during the reduction period. ${staffRequired - reduced} slot(s) left unfilled.`
      } else {
        this.infoTextTarget.innerHTML = `<strong>Note:</strong> All OR slots will be created, but during the reduction period only the reduced number will have staff assigned. Unfilled slots show intentional OR room closures.`
      }
    } else if (mode === "slots") {
      const val = parseInt(document.querySelector("[name='shift[or_reduction_value]']")?.value) || 0
      if (val > 0) {
        const reduced = Math.max(staffRequired - val, 0)
        this.infoTextTarget.innerHTML = `<strong>Summary:</strong> ${val} fewer slot(s) per day. ${reduced} of ${staffRequired} slots assigned during the reduction period.`
      } else {
        this.infoTextTarget.innerHTML = `<strong>Note:</strong> Enter the number of slots to reduce per day during the reduction period.`
      }
    } else if (mode === "hours") {
      const val = parseFloat(document.querySelector("[name='shift[or_reduction_value]']")?.value) || 0
      if (val > 0) {
        const reducedHours = Math.max(duration - val, 0.5)
        this.infoTextTarget.innerHTML = `<strong>Summary:</strong> Shift duration reduced from <strong>${duration}h</strong> to <strong>${reducedHours}h</strong> during the reduction period. All ${staffRequired} staff still assigned — each works the shorter ${reducedHours}h shift.`
      } else {
        this.infoTextTarget.innerHTML = `<strong>Note:</strong> Enter the number of hours to reduce from the shift duration during the reduction period.`
      }
    }
  }

  currentMode() {
    const checkedRadio = this.element.querySelector("input[name='shift[or_reduction_mode]']:checked")
    return checkedRadio?.value || "percentage"
  }

  clearReductionFields() {
    const percentageField = document.querySelector("[name='shift[or_reduction_percentage]']")
    const startDateField = document.querySelector("[name='shift[or_reduction_start_date]']")
    const endDateField = document.querySelector("[name='shift[or_reduction_end_date]']")
    const valueField = document.querySelector("[name='shift[or_reduction_value]']")

    if (percentageField) percentageField.value = ""
    if (startDateField) startDateField.value = ""
    if (endDateField) endDateField.value = ""
    if (valueField) valueField.value = ""

    // Reset radio to percentage
    const percentageRadio = document.querySelector("input[name='shift[or_reduction_mode]'][value='percentage']")
    if (percentageRadio) {
      percentageRadio.checked = true
      this.showPercentageField()
    }
    this.updateInfoText()
  }
}
